import { useState, useEffect, useRef } from "preact/hooks"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Loader2, Columns2, FileSearch, AlertCircle } from "lucide-react"
import { parseFile } from "@/api/deduction"
import { toast } from "@/components/ui/use-toast"
import { AutoSplitOptions, AutoSplitPreferences, getDefaultPreferences } from "./auto_split_preferences"
import { SplitGroupingOptions, type SplitGroupingValue } from "./split_grouping_options"
import { Backup } from "@/api/backup"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select"
import { LedgerAccounts, loadAccountCodes } from "@/accounting/accounts"
import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import {
  PromotionSelector,
  PromotionMatch,
  PromotionSearchContextSeed,
} from "@/accounting_detail/PromotionSelector.tsx"

interface AutoSplitModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  backupData: Backup[]
  backupS3Uri: string | null
  deductionId: string
  savedPreferences: AutoSplitPreferences | null
  orgFieldConfigs?: OrgFieldConfig[]
  promotionSearchContextSeed?: PromotionSearchContextSeed
  onGenerateSplits: (options: AutoSplitOptions) => void | Promise<void>
  onBackupParsed: () => void
}

export function AutoSplitModal({
  open,
  onOpenChange,
  backupData,
  backupS3Uri,
  deductionId,
  savedPreferences,
  orgFieldConfigs = [],
  promotionSearchContextSeed,
  onGenerateSplits,
  onBackupParsed,
}: AutoSplitModalProps) {
  const [parsing, setParsing] = useState(false)
  const parse_in_flight = useRef(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const generate_in_flight = useRef(false)
  const [reasonCode, setReasonCode] = useState<SelectItem | undefined>(undefined)
  const [defaultRetailerInvoice, setDefaultRetailerInvoice] = useState("")
  const [defaultPromotionMatch, setDefaultPromotionMatch] = useState<PromotionMatch | null>(null)
  const ledgerAccounts = LedgerAccounts.use()

  const showRetailerInvoiceField = split_form_org_field_active(orgFieldConfigs, "retailer_invoice_number")
  const showPromotionDefaultField = split_form_org_field_active(orgFieldConfigs, "promotion_remote_ids")

  const defaults = savedPreferences || getDefaultPreferences()
  const [groupBy, setGroupBy] = useState<SplitGroupingValue>({
    retailer: defaults.splitByRetailer,
    productLine: defaults.splitByProductLine,
    executionDate: defaults.splitByExecutionDate,
    adminFee: defaults.splitByAdminFee,
  })

  useEffect(() => {
    if (ledgerAccounts.length === 0) {
      loadAccountCodes()
    }
  }, [])

  useEffect(() => {
    if (open) {
      setDefaultPromotionMatch(null)
    }
  }, [open])

  // Prevent modal from closing when interacting with popovers
  function handleInteractOutside(e: Event) {
    const target = e.target as HTMLElement
    if (
      target?.closest?.("[data-radix-popper-content-wrapper]") ||
      target?.closest?.("[role='listbox']") ||
      target?.closest?.("[cmdk-list]") ||
      target?.closest?.("[cmdk-input]") ||
      target?.closest?.("[cmdk-item]")
    ) {
      e.preventDefault()
    }
  }

  async function handleParseBackup() {
    if (!backupS3Uri || parse_in_flight.current) return
    parse_in_flight.current = true

    setParsing(true)
    setParseError(null)

    try {
      const result = await parseFile(deductionId, backupS3Uri)

      if (result.ok && result.value.data.success) {
        const isQueued = !!result.value.data.message?.toLowerCase().includes("queued")
        if (isQueued) {
          toast({
            title: "Parse queued",
            description: "The file was queued for processing. Try auto-split again once parsing finishes.",
          })
          onOpenChange(false)
        } else {
          toast({ title: "Backup parsed successfully", description: "You can now configure auto-split options." })
          onBackupParsed()
        }
      } else {
        const errorMsg = result.ok ? result.value.data.message || "Failed to parse backup file" : result.error.message
        setParseError(errorMsg)
        toast({ title: "Failed to parse backup", description: errorMsg, variant: "destructive" })
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : "An unexpected error occurred"
      setParseError(errorMsg)
      toast({ title: "Failed to parse backup", description: errorMsg, variant: "destructive" })
    } finally {
      parse_in_flight.current = false
      setParsing(false)
    }
  }

  async function handleGenerate() {
    if (generate_in_flight.current) return
    generate_in_flight.current = true

    setGenerating(true)
    try {
      await onGenerateSplits({
        splitByRetailer: groupBy.retailer,
        splitByProductLine: groupBy.productLine,
        splitByExecutionDate: groupBy.executionDate,
        splitByAdminFee: groupBy.adminFee,
        reasonCodeId: reasonCode?.id,
        reasonCodeName: reasonCode?.expense_account || reasonCode?.name,
        defaultRetailerInvoiceNumber:
          showRetailerInvoiceField && defaultRetailerInvoice.trim() ? defaultRetailerInvoice.trim() : undefined,
        defaultPromotionId:
          showPromotionDefaultField && defaultPromotionMatch ? defaultPromotionMatch.promotion.id : undefined,
      })
      onOpenChange(false)
    } finally {
      generate_in_flight.current = false
      setGenerating(false)
    }
  }

  const hasBackupData = backupData.length > 0
  const hasBackupFile = !!backupS3Uri
  const hasAtLeastOneOption = groupBy.retailer || groupBy.productLine || groupBy.executionDate || groupBy.adminFee

  // State 1: No backup data but backup file exists - offer to parse
  if (!hasBackupData && hasBackupFile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" onInteractOutside={handleInteractOutside}>
          <DialogHeader>
            <DialogTitle>Parse Backup File</DialogTitle>
            <DialogDescription>
              No parsed backup data is available for this transaction. Would you like to parse the backup file first?
            </DialogDescription>
          </DialogHeader>

          {parseError && (
            <div class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{parseError}</span>
            </div>
          )}

          <DialogFooter class="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={parsing}>
              Cancel
            </Button>
            <Button
              onClick={handleParseBackup}
              aria-busy={parsing}
              class={parsing ? "pointer-events-none opacity-70" : undefined}
            >
              {parsing ? (
                <>
                  <ButtonSpinnerIcon />
                  Parsing...
                </>
              ) : (
                <>
                  <FileSearch className="h-4 w-4 mr-2" />
                  Parse Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // State 2: Has backup data - show configuration options
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw*2/3)] max-w-[calc(100vw*2/3)]"
        onInteractOutside={handleInteractOutside}
      >
        <DialogHeader>
          <DialogTitle>Custom Split from Parsed Backup</DialogTitle>
          <DialogDescription>Select the fields to group by when creating splits from the backup data.</DialogDescription>
        </DialogHeader>

        <div class="space-y-4 py-4">
          <SplitGroupingOptions value={groupBy} onChange={setGroupBy} />

          <div class="border-t pt-4 space-y-2">
            <Label class="text-sm font-medium">Ledger Account (optional)</Label>
            <p class="text-xs text-gray-500 mb-2">Select an account code to apply to all generated splits</p>
            <AutocompleteSelect
              onSelect={setReasonCode}
              initial={reasonCode}
              values={ledgerAccounts}
              insideModal
            />
          </div>

          {(showRetailerInvoiceField || showPromotionDefaultField) && (
            <div class="border-t pt-4 space-y-3">
              <Label class="text-sm font-medium">Defaults for all generated splits</Label>
              {showRetailerInvoiceField && (
                <div class="space-y-1">
                  <Label class="text-xs text-gray-600" htmlFor="auto-split-retailer-invoice">
                    Retailer invoice #
                  </Label>
                  <Input
                    id="auto-split-retailer-invoice"
                    className="h-9"
                    value={defaultRetailerInvoice}
                    onInput={(e: Event) => setDefaultRetailerInvoice((e.target as HTMLInputElement).value)}
                    placeholder="Optional"
                  />
                </div>
              )}
              {showPromotionDefaultField && (
                <div class="space-y-1">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <Label class="text-xs text-gray-600">Promotion</Label>
                    {defaultPromotionMatch && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-gray-600"
                        onClick={() => setDefaultPromotionMatch(null)}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <p class="text-xs text-gray-500 mb-1">
                    Same search and scoring as on each split line. Optional—if unset, the transaction&apos;s matched
                    promotion is used when present.
                  </p>
                  <PromotionSelector
                    deductionId={deductionId}
                    onSelect={setDefaultPromotionMatch}
                    selectedPromotionId={defaultPromotionMatch?.promotion.id}
                    searchContextSeed={promotionSearchContextSeed}
                    className="w-full max-w-full"
                  />
                </div>
              )}
            </div>
          )}

          <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p class="text-sm text-blue-700">
              <strong>Note:</strong> Grouping preferences will be saved for next time. Review the generated splits and
              click "Save All" to apply them.
            </p>
          </div>
        </div>

        <DialogFooter class="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!hasAtLeastOneOption}
            aria-busy={generating}
            class={generating ? "pointer-events-none opacity-70" : undefined}
          >
            {generating ? (
              <>
                <ButtonSpinnerIcon />
                Generating…
              </>
            ) : (
              <>
                <Columns2 className="h-4 w-4 mr-2" />
                Generate Splits
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Lucide only merges `className`; Preact `class` skips that merge, so spin lives on a real DOM node. */
function ButtonSpinnerIcon() {
  return (
    <span
      class="mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center motion-safe:animate-spin"
      aria-hidden
    >
      <Loader2 className="h-4 w-4" />
    </span>
  )
}

function split_form_org_field_active(configs: OrgFieldConfig[], field_name: string): boolean {
  return configs.some(
    (c) => c.is_active && c.field_name === field_name && c.table_contexts.includes("split_form")
  )
}
